import { useCallback, useEffect, useMemo, useState } from "react";

import {
  listDealerOrderIntakes,
  type OpsOrderIntakeRow,
  type OpsOrderIntakeStatus,
} from "@/lib/orderIntakeClient";
import type { OrderManufacturerPartner } from "@/lib/sales-intake/manufacturerSubmissionRouting";
import { formatSalesRepOrderRowLabel } from "@/lib/sales-intake/salesRepOrderDisplayLabel";

const PAGE_SIZE = 5;
const OPEN_STATUSES: OpsOrderIntakeStatus[] = ["pending_review", "on_hold"];

type ViewMode = "open" | "all";

type Props = {
  refreshKey: number;
  manufacturers: readonly OrderManufacturerPartner[];
};

export function SalesRepSubmittedOrdersList(props: Props) {
  const [rows, setRows] = useState<OpsOrderIntakeRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>("open");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const manufacturerNameByOrgId = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of props.manufacturers) m.set(p.id, p.name);
    return m;
  }, [props.manufacturers]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listDealerOrderIntakes({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      ...(viewMode === "open" ? { status: OPEN_STATUSES } : {}),
    });
    setLoading(false);
    if (!r.ok) {
      setRows([]);
      setTotal(null);
      setError(r.error);
      return;
    }
    setRows(r.data.rows ?? []);
    setTotal(r.data.total);
  }, [page, viewMode]);

  useEffect(() => {
    void load();
  }, [load, props.refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [viewMode, props.refreshKey]);

  const pageCount =
    total != null && total > 0 ? Math.ceil(total / PAGE_SIZE) : rows.length > 0 ? page + 1 : 0;
  const canPrev = page > 0;
  const canNext = total != null ? (page + 1) * PAGE_SIZE < total : rows.length === PAGE_SIZE;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Your submissions</h3>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <button
            type="button"
            className={
              viewMode === "open"
                ? "font-semibold text-indigo-800 dark:text-indigo-200"
                : "text-gray-600 underline dark:text-zinc-400"
            }
            onClick={() => setViewMode("open")}
          >
            Open / review
          </button>
          <span className="text-gray-400">|</span>
          <button
            type="button"
            className={
              viewMode === "all"
                ? "font-semibold text-indigo-800 dark:text-indigo-200"
                : "text-gray-600 underline dark:text-zinc-400"
            }
            onClick={() => setViewMode("all")}
          >
            All
          </button>
          <button
            type="button"
            className="text-indigo-700 underline dark:text-indigo-300"
            onClick={() => void load()}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-600 dark:text-zinc-400">
        {viewMode === "open"
          ? "Pending review and on-hold orders you submitted (including upstream manufacturer queues)."
          : "All submissions, newest first."}{" "}
        Ops reviewers accept in Order Intake (ops variant) or Operations.
      </p>
      {error ? (
        <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>
      ) : null}
      {!error && !loading && rows.length === 0 ? (
        <p className="mt-2 text-xs text-gray-500 dark:text-zinc-500">No submissions in this view.</p>
      ) : null}
      {rows.length > 0 ? (
        <ul className="mt-3 space-y-2">
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-950/50"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-gray-900 dark:text-zinc-100">
                  {formatSalesRepOrderRowLabel(row, manufacturerNameByOrgId)}
                </span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {row.status.replace(/_/g, " ")}
                </span>
              </div>
              <p className="mt-1 font-mono text-[10px] text-gray-500 dark:text-zinc-500">{row.id}</p>
              {row.status === "on_hold" && row.hold_reason ? (
                <p className="mt-1 text-amber-800 dark:text-amber-200">Hold: {row.hold_reason}</p>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {pageCount > 1 || canNext || canPrev ? (
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-zinc-400">
          <button
            type="button"
            className="underline disabled:opacity-40"
            disabled={!canPrev || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {page + 1}
            {total != null ? ` of ${pageCount}` : ""}
          </span>
          <button
            type="button"
            className="underline disabled:opacity-40"
            disabled={!canNext || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </div>
  );
}
